import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ExceptionSeverity, ExceptionStatus, ExceptionType } from '@/types/asset-audit';
import type { AuditException } from '@/types/asset-audit';
import { format } from 'date-fns';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface ExceptionListViewProps {
  exceptions: AuditException[];
  selectedRowIds: Set<string>;
  onSelectionChange: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  visibleColumns: string[];
}

type SortKey = keyof AuditException | 'severity';
type SortDirection = 'asc' | 'desc' | null;

// Helper function to format exception type
function formatExceptionType(type: ExceptionType): string {
  const typeMap: Partial<Record<ExceptionType, string>> = {
    [ExceptionType.LocationMismatch]: 'Location Mismatch',
    [ExceptionType.ConditionIssue]: 'Condition Issue',
    [ExceptionType.DataDiscrepancy]: 'Data Discrepancy',
    [ExceptionType.NotFound]: 'Not Found',
    [ExceptionType.DuplicateAsset]: 'Duplicate Asset',
    [ExceptionType.ComplianceIssue]: 'Compliance Issue',
    [ExceptionType.MaintenanceRequired]: 'Maintenance Required',
    [ExceptionType.UnauthorizedMovement]: 'Unauthorised Movement',
    [ExceptionType.AssetMissing]: 'Asset Missing',
    [ExceptionType.AssetDamaged]: 'Asset Damaged',
    [ExceptionType.ConditionMismatch]: 'Condition Mismatch',
    [ExceptionType.Other]: 'Other',
  };
  return typeMap[type] || type;
}

// Helper function to format status
function formatStatus(status: ExceptionStatus): string {
  const statusMap: Partial<Record<ExceptionStatus, string>> = {
    [ExceptionStatus.Open]: 'Open',
    [ExceptionStatus.InProgress]: 'In Progress',
    [ExceptionStatus.UnderReview]: 'Under Review',
    [ExceptionStatus.ActionRequired]: 'Action Required',
    [ExceptionStatus.Resolved]: 'Resolved',
    [ExceptionStatus.Closed]: 'Closed',
    [ExceptionStatus.Escalated]: 'Escalated',
  };
  return statusMap[status] || status;
}

// Helper function to get severity order for sorting
function getSeverityOrder(severity: ExceptionSeverity): number {
  const orderMap: Record<ExceptionSeverity, number> = {
    [ExceptionSeverity.Critical]: 0,
    [ExceptionSeverity.High]: 1,
    [ExceptionSeverity.Medium]: 2,
    [ExceptionSeverity.Low]: 3,
  };
  return orderMap[severity] ?? 999;
}

// Helper function to get severity badge styling
function getSeverityBadgeStyle(severity: ExceptionSeverity): string {
  switch (severity) {
    case ExceptionSeverity.Critical:
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case ExceptionSeverity.High:
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    case ExceptionSeverity.Medium:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case ExceptionSeverity.Low:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  }
}

// Helper function to get status badge styling
function getStatusBadgeStyle(status: ExceptionStatus): string {
  switch (status) {
    case ExceptionStatus.Open:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case ExceptionStatus.InProgress:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case ExceptionStatus.ActionRequired:
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    case ExceptionStatus.Resolved:
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  }
}

// Helper function to compare exception values for sorting - extracted to reduce cognitive complexity (S3776)
function compareExceptionValues(aValue: any, bValue: any, direction: 'asc' | 'desc'): number {
  // Handle null/undefined values
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  // Compare string values
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    // Try to parse as dates first
    const aDate = new Date(aValue);
    const bDate = new Date(bValue);
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      return direction === 'asc'
        ? aDate.getTime() - bDate.getTime()
        : bDate.getTime() - aDate.getTime();
    }
    // Fall back to string comparison
    return direction === 'asc'
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  }

  // Compare number values
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  }

  return 0;
}

export function ExceptionListView({
  exceptions,
  selectedRowIds,
  onSelectionChange,
  onSelectAll,
  visibleColumns,
}: Readonly<ExceptionListViewProps>) {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<{ key: SortKey | null; direction: SortDirection }>({
    key: 'severity',
    direction: 'asc',
  });

  // Sort exceptions by severity (Critical first) by default
  const sortedExceptions = useMemo(() => {
    const sorted = [...exceptions];
    
    if (!sortConfig.key || !sortConfig.direction) {
      // Default sort by severity
      return sorted.sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
    }

    // Sort using extracted helper function to reduce cognitive complexity (S3776)
    sorted.sort((a, b) => {
      const aValue = sortConfig.key === 'severity' 
        ? getSeverityOrder(a.severity) 
        : a[sortConfig.key as keyof AuditException];
      const bValue = sortConfig.key === 'severity' 
        ? getSeverityOrder(b.severity) 
        : b[sortConfig.key as keyof AuditException];

      return compareExceptionValues(aValue, bValue, sortConfig.direction!);
    });

    return sorted;
  }, [exceptions, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig((current) => {
      if (current.key === key) {
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (current.direction === 'desc') {
          return { key: null, direction: null };
        }
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortConfig.key !== columnKey) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  const isAllSelected = exceptions.length > 0 && exceptions.every(e => selectedRowIds.has(e.id));
  const isSomeSelected = exceptions.some(e => selectedRowIds.has(e.id)) && !isAllSelected;

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
            <TableRow className="border-t border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
              <TableHead className="h-12 font-semibold relative px-4 py-3">
                <div className="flex items-center justify-center">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={onSelectAll}
                    aria-label="Select all"
                    className={isSomeSelected ? 'data-[state=checked]:bg-primary/50' : ''}
                  />
                </div>
                {/* Column divider */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                  <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                </div>
              </TableHead>
                
                {visibleColumns.includes('severity') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('severity')}>
                    <div className="flex items-center">
                      Severity
                      <SortIcon columnKey="severity" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
                
                {visibleColumns.includes('asset') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('assetCode')}>
                    <div className="flex items-center">
                      Asset
                      <SortIcon columnKey="assetCode" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
                
                {visibleColumns.includes('exceptionType') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('exceptionType')}>
                    <div className="flex items-center">
                      Exception Type
                      <SortIcon columnKey="exceptionType" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
                
                {visibleColumns.includes('description') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('description')}>
                    <div className="flex items-center">
                      Description
                      <SortIcon columnKey="description" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
                
                {visibleColumns.includes('status') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('status')}>
                    <div className="flex items-center">
                      Status
                      <SortIcon columnKey="status" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
                
                {visibleColumns.includes('reportedBy') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('reportedByName')}>
                    <div className="flex items-center">
                      Reported By
                      <SortIcon columnKey="reportedByName" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
                
                {visibleColumns.includes('reportedDate') && (
                  <TableHead className="h-12 font-semibold relative px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('reportedDate')}>
                    <div className="flex items-center">
                      Reported Date
                      <SortIcon columnKey="reportedDate" />
                    </div>
                    {/* Column divider */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedExceptions.map((exception, index) => {
                const isSelected = selectedRowIds.has(exception.id);
                const isCritical = exception.severity === ExceptionSeverity.Critical;
                
                return (
                  <TableRow
                    key={exception.id}
                    className={`
                      ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                      ${isSelected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}
                      ${isCritical ? 'border-l-4 border-l-red-500' : ''}
                      transition-colors cursor-pointer border-b border-slate-100 dark:border-slate-800
                    `}
                    onClick={() => onSelectionChange(exception.id, !isSelected)}
                  >
                    <TableCell className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => onSelectionChange(exception.id, checked as boolean)}
                        aria-label={`Select exception ${exception.id}`}
                      />
                    </TableCell>
                    
                    {visibleColumns.includes('severity') && (
                      <TableCell className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${getSeverityBadgeStyle(exception.severity)}`}>
                          {exception.severity}
                        </span>
                      </TableCell>
                    )}
                    
                    {visibleColumns.includes('asset') && (
                      <TableCell className="px-4 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/assets/${exception.assetId}`);
                          }}
                          className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex flex-col items-start gap-0.5 group text-left"
                        >
                          <span className="flex items-center gap-1">
                            {exception.assetCode}
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                          </span>
                          <span className="text-xs text-muted-foreground">{exception.assetName}</span>
                        </button>
                      </TableCell>
                    )}
                    
                    {visibleColumns.includes('exceptionType') && (
                      <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {formatExceptionType(exception.exceptionType)}
                      </TableCell>
                    )}
                    
                    {visibleColumns.includes('description') && (
                      <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-md truncate">
                        {exception.description}
                      </TableCell>
                    )}
                    
                    {visibleColumns.includes('status') && (
                      <TableCell className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeStyle(exception.status)}`}>
                          {formatStatus(exception.status)}
                        </span>
                      </TableCell>
                    )}
                    
                    {visibleColumns.includes('reportedBy') && (
                      <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {exception.reportedByName || '-'}
                      </TableCell>
                    )}
                    
                    {visibleColumns.includes('reportedDate') && (
                      <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">
                        {format(new Date(exception.reportedDate), 'dd/MM/yyyy')}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
        </Table>
      </div>
    </div>
  );
}
